import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { formatMoney } from '@/utils/currency';

export interface BarcodeLabelData {
  title: string;
  subtitle?: string;
  price?: number;
  barcode: string;
  category?: string;
}

function barcodeSvg(value: string): string {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, value, {
    format: 'CODE128',
    width: 1.4,
    height: 44,
    displayValue: true,
    fontSize: 12,
    margin: 4,
  });
  return svg.outerHTML;
}

export async function printBarcodeLabels(
  labels: BarcodeLabelData[],
  options?: { codeType?: 'Barcode' | 'QR Code'; printPrice?: boolean; printCategory?: boolean }
): Promise<void> {
  if (!labels.length) return;

  const codeType = options?.codeType || 'Barcode';
  const printPrice = options?.printPrice !== false;
  const printCategory = !!options?.printCategory;

  const labelHtmls = await Promise.all(
    labels.map(async (label) => {
      const priceHtml = (printPrice && label.price != null)
        ? `<div class="price">${formatMoney(label.price)}</div>`
        : '';
      const subtitle = label.subtitle
        ? `<div class="subtitle">${escapeHtml(label.subtitle)}</div>`
        : '';
      const categoryHtml = (printCategory && label.category)
        ? `<div class="category">${escapeHtml(label.category)}</div>`
        : '';

      let codeHtml = '';
      if (codeType === 'QR Code') {
        try {
          const qrDataUrl = await QRCode.toDataURL(label.barcode, {
            width: 250,
            margin: 4,
            errorCorrectionLevel: 'M'
          });
          codeHtml = `<div class="qrcode-wrapper"><img src="${qrDataUrl}" alt="QR Code" style="width: 90px; height: 90px;" /></div>`;
        } catch (err) {
          console.error('Failed to generate QR Code', err);
          codeHtml = `<div class="error">QR Code Error</div>`;
        }
      } else {
        codeHtml = `<div class="barcode">${barcodeSvg(label.barcode)}</div>`;
      }

      if (codeType === 'QR Code') {
        return `
          <div class="label qr-label">
            ${codeHtml}
            <div class="title" style="margin-top: 4px;">${escapeHtml(label.title)}</div>
            ${subtitle}
            ${categoryHtml}
            ${priceHtml}
          </div>
        `;
      } else {
        return `
          <div class="label">
            <div class="title">${escapeHtml(label.title)}</div>
            ${subtitle}
            ${categoryHtml}
            ${priceHtml}
            ${codeHtml}
          </div>
        `;
      }
    })
  );

  const labelHtml = labelHtmls.join('');

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Barcode/QR Labels</title>
      <style>
        @page { size: A4; margin: 10mm; }
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
        }
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 8px;
        }
        .sheet {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .label {
          border: 1px dashed #ccc;
          border-radius: 4px;
          padding: 8px 6px 4px;
          text-align: center;
          min-height: 88px;
          break-inside: avoid;
          page-break-inside: avoid;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .title {
          font-size: 11px;
          font-weight: 700;
          line-height: 1.2;
          max-height: 2.4em;
          overflow: hidden;
        }
        .subtitle {
          font-size: 9px;
          color: #555;
          margin-top: 2px;
        }
        .category {
          font-size: 8px;
          color: #666;
          margin-top: 2px;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .price {
          font-size: 10px;
          font-weight: 600;
          margin-top: 2px;
        }
        .barcode {
          width: 100%;
          margin-top: 4px;
        }
        .barcode svg {
          width: 100%;
          height: auto;
        }
        .qrcode-wrapper {
          width: 80px;
          height: 80px;
          margin-top: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .qr-label {
          min-height: 132px;
          padding: 10px 8px 6px;
        }
        .qr-label .qrcode-wrapper {
          width: 90px;
          height: 90px;
        }
      </style>
    </head>
    <body>
      <div class="sheet">${labelHtml}</div>
      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => window.close(), 500);
        };
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
